import { useState, useEffect, useCallback, useRef } from 'react';
import { UserProfile, Quote, QuoteProductItem, QuoteFormState } from '../types';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../utils/telemetry';
import { CATEGORIES } from '../constants/categories';

export interface OfflineQuote {
  id: string;
  user_id: string;
  client_name: string;
  client_phone?: string;
  client_address?: string;
  service_name: string;
  service_description?: string | null;
  category: string;
  material_cost: number;
  time_spent_minutes: number;
  applied_profit_margin: number;
  final_price: number;
  status: string;
  explanation: string;
  products: QuoteProductItem[];
  services: Array<{ name: string; value: number }>;
  created_at: string;
  offline?: boolean;
}

export interface UseQuotePersistenceParams {
  user: UserProfile;
  quotesCount: number;
  plan: 'Grátis' | 'Pro' | 'Especialista' | 'Empresa' | 'Empresarial' | 'Enterprise';
  formData: QuoteFormState;
  finalPrice: number;
  aiExplanation: string;
  budgetReady: boolean;
  onSaveQuote: (quote: Quote) => void;
  buildSnapshotProducts: (products: QuoteProductItem[]) => QuoteProductItem[];
  setIsPaywallOpen: (isOpen: boolean) => void;
  initialData?: {
    materialCost?: number;
    timeSpent?: number;
    serviceName?: string;
    serviceDescription?: string;
    clientName?: string;
    clientPhone?: string;
    clientAddress?: string;
  } | null;
  onClearInitialData?: () => void;
}

export interface UseQuotePersistenceReturn {
  isSaving: boolean;
  saved: boolean;
  setSaved: (saved: boolean) => void;
  hasCreatedQuote: boolean;
  setHasCreatedQuote: (hasCreated: boolean) => void;
  networkError: string | null;
  handleSaveQuote: () => Promise<void>;
  clearPersistenceDrafts: () => void;
  syncOfflineQueue: () => Promise<void>;
}

export function useQuotePersistence({
  user,
  quotesCount,
  plan,
  formData,
  finalPrice,
  aiExplanation,
  budgetReady,
  onSaveQuote,
  buildSnapshotProducts,
  setIsPaywallOpen,
  initialData,
  onClearInitialData
}: UseQuotePersistenceParams): UseQuotePersistenceReturn {
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);

  const [hasCreatedQuote, setHasCreatedQuote] = useState<boolean>(() => {
    if (typeof window === 'undefined') return quotesCount > 0;
    return quotesCount > 0 || localStorage.getItem('@rmax:hasCreatedQuote') === 'true' || localStorage.getItem('rmax_has_saved_quote') === 'true';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setHasCreatedQuote(quotesCount > 0 || localStorage.getItem('@rmax:hasCreatedQuote') === 'true' || localStorage.getItem('rmax_has_saved_quote') === 'true');
  }, [quotesCount]);

  // Sincronização do rascunho de formData em tempo real
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`rmax_draft_form_${user.id}`, JSON.stringify(formData));
    } catch (e) {
      console.error('Error saving draft form state:', e);
    }
  }, [formData, user.id]);

  // Limpeza de rascunhos antigos ao receber dados iniciais
  useEffect(() => {
    if (initialData && onClearInitialData) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(`rmax_draft_messages_${user.id}`);
        localStorage.removeItem('rmax_draft_items');
        localStorage.removeItem(`rmax_draft_explanation_${user.id}`);
      }
      onClearInitialData();
    }
  }, [initialData, onClearInitialData, user.id]);

  const isSavingRef = useRef(false);

  const clearPersistenceDrafts = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(`rmax_draft_form_${user.id}`);
      localStorage.removeItem('rmax_draft_items');
    }
  }, [user.id]);

  const syncOfflineQueue = useCallback(async () => {
    if (typeof window === 'undefined') return;
    const SYNC_LOCK_KEY = 'rmax_sync_lock';
    const lock = localStorage.getItem(SYNC_LOCK_KEY);
    if (lock && Date.now() - Number(lock) < 10000) return; // outra aba está sincronizando
    localStorage.setItem(SYNC_LOCK_KEY, String(Date.now()));
    try {
      const stored = localStorage.getItem('rmax_offline_quotes');
      if (!stored) return;
      const queue = JSON.parse(stored) as OfflineQuote[];
      if (!Array.isArray(queue) || queue.length === 0) return;

      const remaining: OfflineQuote[] = [];
      for (const quote of queue) {
        try {
          const { offline, ...cleanQuote } = quote;
          const { error } = await supabase.from('quotes').insert(cleanQuote);
          if (error) throw error;
        } catch (err) {
          console.error('Failed to sync offline quote:', err);
          remaining.push(quote);
        }
      }

      if (remaining.length > 0) {
        localStorage.setItem('rmax_offline_quotes', JSON.stringify(remaining));
      } else {
        localStorage.removeItem('rmax_offline_quotes');
      }
    } catch (e) {
      console.error('Error syncing offline queue:', e);
    } finally {
      localStorage.removeItem(SYNC_LOCK_KEY);
    }
  }, []);

  // Try to sync offline queue on mount or when browser goes online
  useEffect(() => {
    syncOfflineQueue();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', syncOfflineQueue);
      return () => window.removeEventListener('online', syncOfflineQueue);
    }
  }, [syncOfflineQueue]);

  const handleSaveQuote = useCallback(async () => {
    if (isSavingRef.current || !budgetReady) return;

    // MODO VISITANTE: Pula a gravação no banco e libera o PDF
    if (user.id === 'guest') {
      if (typeof window !== 'undefined') localStorage.setItem('rmax_has_saved_quote', 'true');
      setSaved(true);
      setHasCreatedQuote(true);
      return;
    }

    // BLOQUEIO PAYWALL: Se grátis e já atingiu o limite de 5 orçamentos
    const maxQuotesAllowed = user.maxFreeQuotes || 5;
    if (plan === 'Grátis' && quotesCount >= maxQuotesAllowed) {
      setIsPaywallOpen(true);
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setNetworkError(null);
    const productsWithSnapshot = buildSnapshotProducts(formData.products || []);
    const quoteData: Record<string, unknown> = {
      user_id: user.effectiveWorkspaceId || user.id,
      client_name: formData.clientName || 'Cliente sem nome',
      client_phone: formData.clientPhone,
      client_address: formData.clientAddress,
      service_name: formData.serviceName || 'Serviço Geral',
      service_description: formData.serviceDescription || null,
      category: CATEGORIES.find(c => c.id === formData.categoryIndex)?.name || 'Geral',
      material_cost: formData.materialCost,
      time_spent_minutes: (formData.timeHours * 60) + formData.timeMinutes,
      applied_profit_margin: formData.profitMarginInput / 100,
      final_price: finalPrice,
      status: 'sent',
      explanation: aiExplanation,
      products: productsWithSnapshot,
      services: formData.services,
      created_at: new Date().toISOString()
    };

    try {
      const { data, error } = await supabase.from('quotes').insert(quoteData).select();
      if (!isMountedRef.current) return;
      if (error) throw error;
      trackEvent('orcamento_salvo_sucesso', { price: finalPrice });
      if (typeof window !== 'undefined') {
        localStorage.setItem('rmax_has_saved_quote', 'true');
        localStorage.setItem('@rmax:hasCreatedQuote', 'true');
      }
      setSaved(true);
      setHasCreatedQuote(true);
      if (onSaveQuote && data && data[0]) onSaveQuote(data[0]);
    } catch (e: unknown) {
      if (!isMountedRef.current) return;
      console.error(e);
      const errMsg = e instanceof Error ? e.message : 'Erro de conexão';
      setNetworkError(errMsg);
      // Fallback offline caso o Supabase falhe (ex: sem rede)
      try {
        const offlineQuote: OfflineQuote = {
          user_id: String(quoteData.user_id),
          client_name: String(quoteData.client_name),
          client_phone: quoteData.client_phone ? String(quoteData.client_phone) : undefined,
          client_address: quoteData.client_address ? String(quoteData.client_address) : undefined,
          service_name: String(quoteData.service_name),
          service_description: quoteData.service_description ? String(quoteData.service_description) : null,
          category: String(quoteData.category),
          material_cost: Number(quoteData.material_cost),
          time_spent_minutes: Number(quoteData.time_spent_minutes),
          applied_profit_margin: Number(quoteData.applied_profit_margin),
          final_price: Number(quoteData.final_price),
          status: String(quoteData.status),
          explanation: String(quoteData.explanation),
          products: quoteData.products as QuoteProductItem[],
          services: quoteData.services as Array<{ name: string; value: number }>,
          created_at: String(quoteData.created_at),
          id: crypto.randomUUID(),
          offline: true
        };
        const offlineQueue = (() => {
          try {
            const stored = localStorage.getItem('rmax_offline_quotes');
            const parsed = stored ? JSON.parse(stored) : [];
            return Array.isArray(parsed) ? parsed : [];
          } catch (parseError) {
            console.warn('R MAX: Invalid offline quote queue storage, resetting.', parseError);
            return [];
          }
        })();
        offlineQueue.push(offlineQuote);
        localStorage.setItem('rmax_offline_quotes', JSON.stringify(offlineQueue));
        trackEvent('orcamento_salvo_offline_sucesso', { price: finalPrice });
        alert('Você parece estar sem internet. O orçamento foi salvo localmente e o R-Max vai gerar seu arquivo.');
        if (typeof window !== 'undefined') {
          localStorage.setItem('rmax_has_saved_quote', 'true');
          localStorage.setItem('@rmax:hasCreatedQuote', 'true');
        }
        setSaved(true);
        setHasCreatedQuote(true);
        const mappedOfflineQuote: Quote = {
          id: offlineQuote.id,
          userId: offlineQuote.user_id,
          clientName: offlineQuote.client_name,
          clientPhone: offlineQuote.client_phone,
          clientAddress: offlineQuote.client_address,
          rawServiceName: offlineQuote.service_name,
          category: offlineQuote.category,
          materialCost: offlineQuote.material_cost,
          timeSpentMinutes: offlineQuote.time_spent_minutes,
          appliedProfitMargin: offlineQuote.applied_profit_margin,
          finalPrice: offlineQuote.final_price,
          status: 'sent',
          createdAt: offlineQuote.created_at,
          explanation: offlineQuote.explanation,
          statusHistory: [],
          products: offlineQuote.products,
          services: offlineQuote.services
        };
        if (onSaveQuote) onSaveQuote(mappedOfflineQuote);
      } catch (localErr) {
        alert('Erro ao salvar localmente. Verifique o espaço do navegador.');
        throw e;
      }
    } finally {
      isSavingRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [budgetReady, user.id, user.maxFreeQuotes, plan, quotesCount, formData, finalPrice, aiExplanation, buildSnapshotProducts, onSaveQuote, setIsPaywallOpen]);

  return {
    isSaving,
    saved,
    setSaved,
    hasCreatedQuote,
    setHasCreatedQuote,
    networkError,
    handleSaveQuote,
    clearPersistenceDrafts,
    syncOfflineQueue
  };
}
